import { ref } from 'vue';
import { useStorage } from '@vueuse/core';
import type { API_User } from '@/api/types';
import { defineStore } from 'pinia';
import api from '@/api/api';

export const useAccountStore = defineStore('account', () => {
  // In-memory only — sensitive, never persisted
  const account = ref({
    currentUser: null as API_User | null,
    role: '',
    permissions: [] as string[],
    avatarUrl: '',
  });

  // Persisted — non-sensitive UI preferences
  const profile = useStorage('account.profile', {
    theme: 'dark',
    language: 'en-US',
  });

  const verifyAuthentication = async (): Promise<boolean> => {
    const token = localStorage.getItem('nh-token');
    if (!token) return false;

    return api
      .request('auth/verify')
      .fetch()
      .then((response) => {
        if (response.ok) return true;
        clearAuthState();
        return false;
      })
      .catch(() => false);
  };

  const updateUserProfile = async (force = false): Promise<void> => {
    if (!force && account.value.currentUser) return Promise.resolve();

    return api
      .request('auth/profile')
      .fetch()
      .then((response) => {
        if (response.ok) {
          account.value.currentUser = response.data.user;
          account.value.role = response.data.role;
          account.value.permissions = response.data.permissions || [];
          account.value.avatarUrl = response.data.avatar_url || '';
        } else {
          console.error('Failed to update user profile:', response.data?.message || 'Unknown error');
          clearAuthState();
        }
      })
      .catch((error) => {
        console.error('Error updating user profile:', error);
      });
  };

  const clearAuthState = () => {
    account.value.currentUser = null;
    account.value.role = '';
    account.value.permissions = [];
    account.value.avatarUrl = '';
    localStorage.removeItem('nh-token');
    window.location.href = '/auth';
  };

  const updateProfile = (settings: Partial<{ theme: string; language: string }>) => {
    if (settings.theme !== undefined) profile.value.theme = settings.theme;
    if (settings.language !== undefined) profile.value.language = settings.language;
  };

  return {
    account,
    profile,
    verifyAuthentication,
    updateUserProfile,
    clearAuthState,
    updateProfile,
  };
});
